import { cn } from '@change/ds-utils';

import { Button } from '@change/ds-core-ui/next';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import { Translate, TranslatePlural } from '@change-corgi/core/react/i18n';
// eslint-disable-next-line internal-rules/no-restricted-design-system-imports
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';
// eslint-disable-next-line internal-rules/no-restricted-design-system-imports
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { BasicCard } from './components/BasicCard';
import { ExpandedCard } from './components/ExpandedCard';
import { FeaturedCard } from './components/FeaturedCard';
import { usePageBody } from './hooks/usePageBody';

const I18N: Record<'guides' | 'news', { fetchedMore: TranslationKey; fetchingMore: TranslationKey }> = {
	guides: {
		fetchedMore: translationKey('corgi.guides.list.fetched_more'),
		fetchingMore: translationKey('corgi.guides.list.fetching_more'),
	},
	news: {
		fetchedMore: translationKey('corgi.news.list.fetched_more'),
		fetchingMore: translationKey('corgi.news.list.fetching_more'),
	},
};

export function PageBody() {
	const {
		data: { hasNextPage, items, nextPageLoading, guides },
		actions: { loadMore },
	} = usePageBody();
	const { containerRef, loadMoreRef, loadingRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	const i18n = I18N[guides ? 'guides' : 'news'];

	return (
		<div className="container px-0 pb-20" ref={containerRef}>
			<ul>
				{items.map((item, index) => {
					const fullWidth = index === 0 || index === 7;
					return (
						<li
							key={item.id}
							className={cn(
								'mb-[30px] inline-block align-top md:mb-[60px]',
								fullWidth ? 'w-full' : 'w-full px-4 md:mx-2 md:w-[calc(33.33%-16px)] md:px-0',
							)}
						>
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<VisuallyHidden role="alert">
									<TranslatePlural
										value={i18n.fetchedMore}
										count={items.length - lastEvent.previousItemsCount}
										replacements={{ count: items.length - lastEvent.previousItemsCount }}
									/>
								</VisuallyHidden>
							)}
							{(() => {
								if (index === 0) return <FeaturedCard item={item} ref={getListItemRef(index)} />;
								if (index === 7) return <ExpandedCard item={item} ref={getListItemRef(index)} />;
								return <BasicCard item={item} ref={getListItemRef(index)} />;
							})()}
						</li>
					);
				})}
			</ul>
			{nextPageLoading && (
				<>
					<VisuallyHidden role="alert">
						<Translate value={i18n.fetchingMore} />
					</VisuallyHidden>
					{/* 44 is the height of the button */}
					<Loader size={44} mx="auto" ref={loadingRef} mt={24} />
				</>
			)}
			{hasNextPage && !nextPageLoading && (
				<div className="flex justify-center">
					<Button
						className="mt-6 w-full max-w-[600px]"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: items.length });
							loadMore();
						}}
						ref={loadMoreRef}
						data-qa="news-and-guides-load-more-button"
					>
						<Translate value="fe.components.load_more.load_more" />
					</Button>
				</div>
			)}
		</div>
	);
}
